import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";
import { UserMgmtService } from "../user.service";
import * as moment from 'moment';

@Component({
  selector: "app-user-analytics-detail",
  templateUrl: "./user-analytics-detail.component.html",
  styleUrls: ["./user-analytics-detail.component.scss"],
})
export class UserAnalyticsDetailComponent implements OnInit, OnDestroy {
  isLoading = false;
  userId: string;
  userDetails: any = null;
  selectedTrendFilter: string = 'all';
  bookingList: any[] = [];
  userCounts: any = null;
  barHeights: number[] = [0, 0, 0, 0, 0, 0, 0]; // Mon, Tue, Wed, Thu, Fri, Sat, Sun
  rawCounts: number[] = [0, 0, 0, 0, 0, 0, 0];
  startDate: any = null;
  endDate: any = null;

  private sub: Subscription;

  constructor(private route: ActivatedRoute, private userMgmtService: UserMgmtService) {}

  ngOnInit() {
    this.sub = this.route.params.subscribe((params) => {
      this.userId = params["id"];
      if (this.userId) {
        this.onTrendFilterChange();
      }
    });
  }

  onTrendFilterChange() {
    const end = moment();
    const start = moment();

    switch (this.selectedTrendFilter) {
      case 'all':
        start.set({ year: 2020, month: 0, date: 1 });
        end.set({ year: 2050, month: 11, date: 31 });
        break;
      case 'lastWeek':
        start.subtract(7, 'days');
        break;
      case 'lastMonth':
        start.subtract(1, 'month');
        break;
      case 'last3Month':
        start.subtract(3, 'months');
        break;
      case 'last6Month':
        start.subtract(6, 'months');
        break;
      case 'customRange':
        // Don't modify start/end, wait for user input
        return;
    }

    this.startDate = start.toDate();
    this.endDate = end.toDate();

    if (this.userId) {
      this.fetchUserDetails(this.userId);
    }
  }

  onCustomDateChange() {
    if (this.startDate && this.endDate && this.userId) {
      this.fetchUserDetails(this.userId);
    }
  }

  fetchUserDetails(id: string) {
    this.isLoading = true;
    this.userDetails = null;

    this.userMgmtService.getUserListApi("userId=" + id + "&expand=addresses&expand=bookingData").subscribe(
      (res) => {
        this.isLoading = false;
        if (res["statusCode"] === 200) {
          const list = res["data"]["userList"] || [];
          this.userDetails = list.length ? list[0] : null;
        } else {
          this.userDetails = null;
        }
      },
      (err) => {
        this.isLoading = false;
        this.userDetails = null;
      }
    );

    // Call user counts API and console log
    this.userMgmtService.getUserCountsApi().subscribe((res) => {
      console.log("User Counts Response:", res);
      this.userCounts = res;
    });

    // Call booking API and console log
    const bookingQuery = `userId=${id}&bookingStartDate=${this.formatDate(this.startDate)}&bookingEndDate=${this.formatDate(this.endDate)}&fetchAll=true`;
    this.userMgmtService.getBookingListApi(bookingQuery).subscribe((res) => {
      console.log("User Bookings Response:", res);
      if (res["statusCode"] === 200) {
        this.bookingList = res["data"] || [];
        this.generateChartData();
      }
    });
  }

  formatDate(date: any): string {
    if (!date) return "";
    const d = new Date(date);
    let month = "" + (d.getMonth() + 1);
    let day = "" + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  }

  generateChartData() {
    this.rawCounts = [0, 0, 0, 0, 0, 0, 0];
    this.barHeights = [0, 0, 0, 0, 0, 0, 0];
    if (this.bookingList.length === 0) return;

    this.bookingList.forEach(booking => {
      if (booking.date) {
        const date = new Date(booking.date);
        let day = date.getDay(); // 0 (Sun) to 6 (Sat)
        // Convert to Mon (0) to Sun (6)
        let index = day === 0 ? 6 : day - 1;
        this.rawCounts[index]++;
      }
    });

    const max = Math.max(...this.rawCounts);
    if (max > 0) {
      // Scale to max 120px for better visibility
      this.barHeights = this.rawCounts.map(h => (h / max) * 120);
    }
  }

  getTotalBookings(): number {
    if (!this.userDetails || !this.userDetails.bookingData || !this.userDetails.bookingData.howOftens) {
      return 0;
    }
    const values = Object.values(this.userDetails.bookingData.howOftens) as any[];
    return values.reduce((acc: number, val: any) => acc + (val || 0), 0);
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }
}
